import { useMemo } from 'react';
import { Body1Strong } from '@fluentui/react-components';

import { TFile, TFileVersion } from '../types';
import {
  TDisplayItem,
  SidebarTree,
  buildTreeItems,
  TSidebarTreeSection,
  ReplicaListingsSection,
} from '@shared/components';
import { buildHref } from '@shared/utils';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';
import { TReplica } from '@modules/replicas';
import { TMap } from '@shared/types';

type TProps = {
  file: TFile;
  version?: TFileVersion;
  replica?: TReplica;
};

const NavFileInsights = ({ file, version, replica }: TProps) => {
  // in-refs:
  const inRefs: TDisplayItem[] = useMemo(() => {
    if (!version) return [];

    const counter: TMap<string, number> = {};
    return file.inRefs
      .filter((ref) => (ref.versionId ? version.id === ref.versionId : file.versions[0] === version))
      .map((ref) => {
        counter[ref.sourceDocId] = (counter[ref.sourceDocId] || 0) + 1;
        return {
          id: ref.id,
          text:
            getReferenceItemLabel(ref.sourceDocName) +
            (counter[ref.sourceDocId] > 1 ? ` (${counter[ref.sourceDocId]})` : ''),
          href: buildHref(`/document/${ref.sourceDocId}`, { view: 'latest', elem: ref.id }),
        };
      });
  }, [version, file.inRefs, file.versions]);

  const insightSections = useMemo<TSidebarTreeSection[]>(() => {
    if (!version) return [];

    return [
      {
        id: 'inRefs',
        label: <Body1Strong>Other documents referencing v{version.id}</Body1Strong>,
        items: buildTreeItems(inRefs, 'inRefs'),
      },
    ];
  }, [version, inRefs]);

  const insightsTreeProps = useMemo(
    () => (insightSections.length > 0 ? { defaultOpenItems: insightSections.map((section) => section.id) } : undefined),
    [insightSections]
  );

  if (!version) return <p className="pl-5">No version found.</p>;

  return (
    <div className="mx-2">
      <div className="px-2">
        <SidebarTree ariaLabel="File insights" sections={insightSections} treeProps={insightsTreeProps} />
      </div>

      <ReplicaListingsSection replica={replica} replicaListings={file.replicaListings} versions={file.versions} />
    </div>
  );
};

export default NavFileInsights;
